import React from "react";
import { ChartBarIcon } from "./icons";
import { SectionTimeAnalytics as SectionTimeAnalyticsType } from "../types";
import { formatSectionName, formatDuration } from "../utils/analytics";

interface SectionTimeAnalyticsProps {
  sectionAnalytics: SectionTimeAnalyticsType[];
  totalOfferViewTime: number;
  loading?: boolean;
}

interface SectionTimeAnalyticsSkeletonProps {}

const SectionTimeAnalyticsSkeleton: React.FC<
  SectionTimeAnalyticsSkeletonProps
> = () => {
  return (
    <div className="card p-6">
      <div className="animate-pulse">
        <div className="h-6 bg-neutral-200 rounded w-48 mb-4"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="h-4 bg-neutral-200 rounded w-32"></div>
                <div className="flex items-center space-x-3">
                  <div className="h-4 bg-neutral-200 rounded w-16"></div>
                  <div className="h-4 bg-neutral-200 rounded w-12"></div>
                </div>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-2">
                <div className="bg-neutral-300 h-2 rounded-full w-1/3"></div>
              </div>
              <div className="flex justify-between">
                <div className="h-3 bg-neutral-200 rounded w-20"></div>
                <div className="h-3 bg-neutral-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SectionTimeAnalytics: React.FC<SectionTimeAnalyticsProps> = ({
  sectionAnalytics,
  totalOfferViewTime,
  loading = false,
}) => {
  if (loading) {
    return <SectionTimeAnalyticsSkeleton />;
  }

  if (!sectionAnalytics || sectionAnalytics.length === 0) {
    return (
      <div className="card p-6 text-center">
        <ChartBarIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">
          Brak danych o sekcjach
        </h3>
        <p className="text-sm text-neutral-600">
          Dane o czasie spędzonym w sekcjach pojawią się po pierwszych
          wyświetleniach.
        </p>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Czas spędzony w sekcjach
      </h3>
      <div className="space-y-4">
        {sectionAnalytics.map((section) => (
          <div key={section.sectionName} className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-neutral-700">
                {formatSectionName(section.sectionName)}
              </span>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-neutral-600">
                  {formatDuration(Math.round(section.averageTimeMs / 1000))} śr.
                </span>
                <span className="text-sm font-semibold text-neutral-900">
                  {section.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${section.percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-neutral-500">
              <span>
                {formatDuration(Math.round(section.totalTimeMs / 1000))} łącznie
              </span>
              <span>{section.sessionCount} sesji</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionTimeAnalytics;
